import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../Layout/Layout';
import { getMarketplaceBots } from '../../services/MarketplaceService';
import './MarketPlace.css';

const PAGE_SIZE = 10;

function formatProfit(value, quoteAsset = '') {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) return `0.0000 ${quoteAsset}`.trim();
    const prefix = parsed > 0 ? '+' : '';
    return `${prefix}${parsed.toFixed(4)} ${quoteAsset}`.trim();
}

function getProfitClass(value) {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed) || parsed === 0) return 'marketplace-pnl--neutral';
    return parsed > 0 ? 'marketplace-pnl--positive' : 'marketplace-pnl--negative';
}

function MarketplaceRow({ bot }) {
    const performance = bot.performance || {};

    return (
        <tr>
            <td>
                <span className="marketplace-bot-name">{bot.name}</span>
                <span className="marketplace-owner">by {bot.ownerName}</span>
            </td>
            <td>{bot.symbol}</td>
            <td>
                <span className={`marketplace-status marketplace-status--${bot.isActive ? 'running' : 'stopped'}`}>
                    {bot.isActive ? 'Running' : 'Stopped'}
                </span>
            </td>
            <td>{performance.totalTrades ?? 0}</td>
            <td>{performance.activeTrades ?? 0}</td>
            <td>{performance.exitedTrades ?? 0}</td>
            <td className={`marketplace-pnl ${getProfitClass(performance.totalProfitLoss)}`}>
                {formatProfit(performance.totalProfitLoss, bot.quoteAsset)}
            </td>
        </tr>
    );
}

const MarketPlace = () => {
    const [bots, setBots] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [symbol, setSymbol] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        setIsLoading(true);
        setError('');
        getMarketplaceBots(symbol, page)
            .then((result) => {
                setBots(result.rows || []);
                setCount(result.count || 0);
            })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? String(err.response.data) : err.message);
                setBots([]);
                setCount(0);
            })
            .finally(() => setIsLoading(false));
    }, [symbol, page]);

    function handleSearchSubmit(event) {
        event.preventDefault();
        setPage(1);
        setSymbol(searchInput.trim().toUpperCase());
    }

    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

    return (
        <Layout>
            <div className="marketplace-page">
                <header className="marketplace-page-header">
                    <span className="marketplace-page-eyebrow">Community bots</span>
                    <h1 className="marketplace-page-title">Bot marketplace</h1>
                    <p className="marketplace-page-subtitle">
                        Browse publicly shared algo bots from NeuraTrader users.
                        Compare names, trading pairs, and live profit/loss performance.
                    </p>
                </header>

                <div className="marketplace-toolbar">
                    <form className="marketplace-search" onSubmit={handleSearchSubmit}>
                        <label htmlFor="marketplace-symbol">Filter by pair</label>
                        <input
                            id="marketplace-symbol"
                            type="text"
                            placeholder="e.g. BTCUSDT"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                    </form>
                    <span className="marketplace-count">
                        {count} public {count === 1 ? 'bot' : 'bots'}
                    </span>
                </div>

                <div className="marketplace-card">
                    {isLoading ? (
                        <div className="marketplace-loading">Loading public bots…</div>
                    ) : null}

                    {!isLoading && error ? (
                        <div className="marketplace-error">{error}</div>
                    ) : null}

                    {!isLoading && !error && bots.length === 0 ? (
                        <div className="marketplace-empty">
                            No public bots found{symbol ? ` for ${symbol}` : ''}.
                            Share your algo bot as <strong>Public</strong> in Bot setup to list it here.
                        </div>
                    ) : null}

                    {!isLoading && !error && bots.length > 0 ? (
                        <>
                            <div className="marketplace-table-wrap">
                                <table className="marketplace-table">
                                    <thead>
                                        <tr>
                                            <th>Bot</th>
                                            <th>Pair</th>
                                            <th>Status</th>
                                            <th>Total trades</th>
                                            <th>Active</th>
                                            <th>Exited</th>
                                            <th>Profit / loss</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bots.map((bot) => (
                                            <MarketplaceRow key={bot.id} bot={bot} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {count > PAGE_SIZE ? (
                                <div className="marketplace-pagination">
                                    <button
                                        type="button"
                                        disabled={page <= 1}
                                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                    >
                                        Previous
                                    </button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button
                                        type="button"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                    >
                                        Next
                                    </button>
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </div>

                <p className="marketplace-note">
                    Performance is calculated from filled buy and sell orders since each bot started.
                    Sign in to copy shared bots from your dashboard, or{' '}
                    <Link to="/register" className="text-primary">create an account</Link>
                    {' '}to publish your own.
                </p>
            </div>
        </Layout>
    );
};

export default MarketPlace;